/**
 * 渠道后台角色与权限点(OEM 第二批 A4,设计稿 §3.4)+ 增值舱面板的纯函数(A2 / A3)。
 *
 * 权限点名与后端 `PartnerPerm` 一一对应;前端只用来过滤导航 / 路由 / 按钮(体验),
 * 服务端每个 /partner/** 接口仍独立判权(403 PARTNER_ROLE_FORBIDDEN)。
 */
import type {
  AddonHolding,
  AddonPurchasable,
  AddonRetireItem,
  PartnerModuleItem,
  TenantAddons,
} from "../api/partnerPortalService";

export const PARTNER_PERMS = [
  "PORTAL_READ",
  "TENANT_READ",
  "TENANT_WRITE",
  "ORDER",
  "CREDIT_MOVE",
  "WALLET_BALANCE",
  "WALLET_EVENTS",
  "SETTLEMENT",
  "ANNUAL_FEE",
  "USAGE",
  "AUDIT",
  "BRAND_WRITE",
  "ACCOUNT_MANAGE",
] as const;
export type PartnerPerm = (typeof PARTNER_PERMS)[number];

/**
 * 判权。perms = null 表示「还不知道 / 老后端没下发」→ 放行(服务端仍会拦);
 * 空数组 = 明确无任何权限(脏角色)。
 */
export function partnerCan(
  perms: readonly string[] | null | undefined,
  perm: PartnerPerm
): boolean {
  return perms == null || perms.includes(perm);
}

// ── 增值舱 / 模块面板 ──────────────────────────────────────────────────

export interface OwnedModuleRow {
  module: PartnerModuleItem;
  /** PLAN = 套餐自带;ADDON = 增值舱。 */
  ownedBy: "PLAN" | "ADDON";
  /** ADDON 的持有行(到期日、来源);PLAN 为 null。 */
  holding: AddonHolding | null;
}

export interface PurchasableRow {
  capability: string;
  name: string;
  entry: AddonPurchasable;
}

export interface UnavailableRow {
  capability: string;
  name: string;
  /** 后端 reasonText;没有目录条目(本渠道不可售)时为 null,页面给通用文案。 */
  reasonText: string | null;
}

export interface GroupedModules {
  owned: OwnedModuleRow[];
  purchasable: PurchasableRow[];
  unavailable: UnavailableRow[];
}

/**
 * 账户资源上限项(与 entities/tenant RESOURCE_CAP_KEYS 同一份;entity 之间不互相 import,这里抄一份)。
 * 它们是额度,不是舱,渠道「模块与配额」只在配额表里展示。
 */
export const ACCOUNT_RESOURCE_CAPS = ["seat", "advisor", "dh_voice_profile"] as const;

export function isAccountResourceCap(key: string): boolean {
  return (ACCOUNT_RESOURCE_CAPS as readonly string[]).includes(key);
}

/**
 * 合并 /tenants/{id}/modules 与 /billing/tenants/{code}/addons(按 capability):
 *  - 已拥有 = modules.ownedBy ∈ {PLAN, ADDON}(暂停的也算已拥有);
 *  - 可加购 = 未拥有且 catalog.purchasable;
 *  - 不可加购 = 未拥有且(catalog 不可买 → reasonText;或根本不在本渠道可售目录里)。
 * 目录里有、modules 里没有的舱(新舱 / 未开放)也按 catalog 归类,名字取 SKU 名。
 */
export function groupTenantModules(
  modules: readonly PartnerModuleItem[],
  addons: TenantAddons | null | undefined
): GroupedModules {
  const catalog = new Map<string, AddonPurchasable>();
  for (const c of addons?.catalog ?? []) catalog.set(c.sku.capability, c);
  const holdings = new Map<string, AddonHolding>();
  for (const h of addons?.holdings ?? []) {
    const cur = holdings.get(h.capability);
    // 同舱多行时优先 ACTIVE
    if (!cur || (cur.status !== "ACTIVE" && h.status === "ACTIVE")) holdings.set(h.capability, h);
  }

  const out: GroupedModules = { owned: [], purchasable: [], unavailable: [] };
  const seen = new Set<string>();
  for (const m of modules) {
    seen.add(m.key);
    // 1004:席位 / 人设授权 / 声纹条数是账户资源上限(随套餐给额度),不是舱 —— 不进「已拥有 / 可加购 / 不可加购」,
    // 只在下方配额表里出现。此前它们 ownedBy 为空,被归到「不可加购 · 套餐不含」,与配额表里的 2 个额度自相矛盾。
    if (isAccountResourceCap(m.key) && !catalog.has(m.key)) continue;
    const ownedBy: "PLAN" | "ADDON" | null =
      m.ownedBy === "PLAN" ? "PLAN" : m.ownedBy === "ADDON" ? "ADDON" : null;
    if (ownedBy) {
      out.owned.push({
        module: m,
        ownedBy,
        holding: ownedBy === "ADDON" ? (holdings.get(m.key) ?? null) : null,
      });
      continue;
    }
    const c = catalog.get(m.key);
    if (c?.purchasable) out.purchasable.push({ capability: m.key, name: m.name, entry: c });
    else
      out.unavailable.push({ capability: m.key, name: m.name, reasonText: c?.reasonText ?? null });
  }
  for (const c of addons?.catalog ?? []) {
    const cap = c.sku.capability;
    if (seen.has(cap)) continue;
    seen.add(cap);
    if (c.purchasable) out.purchasable.push({ capability: cap, name: c.sku.displayName, entry: c });
    else
      out.unavailable.push({ capability: cap, name: c.sku.displayName, reasonText: c.reasonText });
  }
  return out;
}

/**
 * 续费时可以「一起续」的增值舱(与后端 defaultRenewCodes 同口径):
 * 有 SKU、非赠送;ACTIVE,或 EXPIRED 且与上期同时到期。
 */
export function renewableAddonHoldings(
  holdings: readonly AddonHolding[],
  currentPeriodEnd: string | null | undefined
): AddonHolding[] {
  return holdings.filter((h) => {
    if (!h.addonCode || h.source === "GIFT") return false;
    if (h.status === "ACTIVE") return true;
    return h.status === "EXPIRED" && !!currentPeriodEnd && h.endAt === currentPeriodEnd;
  });
}

/** 换档将失效的增值舱里是否有降档项(须勾选确认)。 */
export function retireNeedsAck(items: readonly AddonRetireItem[] | null | undefined): boolean {
  return (items ?? []).some((i) => i.reason === "NOT_ELIGIBLE");
}

/** 对账单 breakdownJson 里的增值舱采购(只展示,不计分成)。没有 / 解析失败 → null。 */
export interface AddonPurchaseSummary {
  credits: number;
  count: number;
  refundCredits: number;
}

export function parseAddonPurchases(
  breakdownJson: string | null | undefined
): AddonPurchaseSummary | null {
  if (!breakdownJson) return null;
  try {
    const obj = JSON.parse(breakdownJson) as Record<string, unknown>;
    const a = obj?.addonPurchases as Partial<AddonPurchaseSummary> | undefined;
    if (!a || typeof a !== "object") return null;
    return {
      credits: Number(a.credits ?? 0),
      count: Number(a.count ?? 0),
      refundCredits: Number(a.refundCredits ?? 0),
    };
  } catch {
    return null;
  }
}
